import Enums from "../utils/enums";
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberEmptyOrZero, isNumberLessThanOrEqualToZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { toFormattedMoney, toFormattedPercentage } from "../utils/stringFormatter";
import { create, getAll, getByDoubleQuery, getById, getByIdQuery, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import moment from "moment-timezone";
import { randomCodeGenerator } from "../utils/orderUtils";
import { toLower } from "lodash";
import { normalizeCouponDiscountRows, validateCouponDiscountRows } from "../utils/couponUtils";

class Coupon {
    static collectionName = 'coupons';

    code = toLower(randomCodeGenerator(10));
    discountType = "FLAT_AMOUNT"; // percentage, flat_amount
    discountAmount = 0.0; // decimal percentage or flat amount
    discountTarget = "CART"; // discounts the whole cart or specific item(s) cart, product
    productIds = []; // if applies to specific products, which products
    active = false;

    usageCount = 0; // How many times the coupon has been used.
    expiresAt = null;
    individualUse = false; // Individual use means this coupon cannot be used in conjunction with other coupons.
    usageLimit = 0; // Amount of times this coupon can be used globally. 0 = unlimited
    usageLimitPerUser = 1; // Amount of times this coupon can be used by a customer.
    limitUsageToXItems = 0; // 	The number of products in your cart this coupon can apply to (for product discounts).
    freeShipping = false; // Does this coupon grant free shipping?
    excludeSaleItems = false; // Excluding sale items mean this coupon cannot be used on items that are on sale (or carts that contain on sale items).
    minimumAmount = 0; // Minimum spend amount that must be met before this coupon can be used.
    maximumAmount = 0; // Maximum spend amount that must be met before this coupon can be used.
    emailRestrictions = []; // Only customers with a matching email address can use the coupon.
    referralUserId = null;
    referralAffiliateId = null;
    referralSubAffiliateId = null;
    usedBy = [];

    excludedProductIds = [];
    excludedVariantIds = [];
    applyToBundle = false;
    excludedBundleCounts = [];

    newCustomersOnly = false;
    starterTitrationLevelOnly = false;

    creditPayout = 0;
    cashPayout = 0;

    terms = null;

    discounts = []; // {productId, variantIds, discountType, discountAmount} if discountTarget is MIXED or MIXED_RECURRING, define different rules for different products, if flat amounts are specified, each product will have its own flat amount discounted

    isReferral = false;

    constructor(coupon) {
        if (isObjectNotEmpty(coupon)) {
            if (isStringNotEmpty(coupon.code)) { this.code = toLower(coupon.code); }
            if (isStringNotEmpty(coupon.discountType)) { this.discountType = coupon.discountType; }
            if (isNumberNotEmpty(coupon.discountAmount)) { this.discountAmount = coupon.discountAmount; }
            if (isStringNotEmpty(coupon.discountTarget)) { this.discountTarget = coupon.discountTarget; }
            if (isListNotEmpty(coupon.productIds)) { this.productIds = coupon.productIds; }
            if (isBooleanTrue(coupon.active)) { this.active = true; }

            if (isNumberNotEmpty(coupon.usageCount)) { this.usageCount = coupon.usageCount; }
            if (isNotNullOrUndefined(coupon.expiresAt)) { this.expiresAt = coupon.expiresAt; }
            if (isBooleanTrue(coupon.individualUse)) { this.individualUse = true; }
            if (isNumberNotEmpty(coupon.usageLimit)) { this.usageLimit = coupon.usageLimit; }
            if (isNumberNotEmpty(coupon.usageLimitPerUser)) { this.usageLimitPerUser = coupon.usageLimitPerUser; }
            if (isNumberNotEmpty(coupon.limitUsageToXItems)) { this.limitUsageToXItems = coupon.limitUsageToXItems; }
            if (isBooleanTrue(coupon.freeShipping)) { this.freeShipping = true; }
            if (isBooleanTrue(coupon.excludeSaleItems)) { this.excludeSaleItems = true; }
            if (isNumberNotEmpty(coupon.minimumAmount)) { this.minimumAmount = coupon.minimumAmount; }
            if (isNumberNotEmpty(coupon.maximumAmount)) { this.maximumAmount = coupon.maximumAmount; }
            if (isListNotEmpty(coupon.emailRestrictions)) { this.emailRestrictions = coupon.emailRestrictions; }
            if (isStringNotEmpty(coupon.referralUserId)) { this.referralUserId = coupon.referralUserId; }
            if (isStringNotEmpty(coupon.referralAffiliateId)) { this.referralAffiliateId = coupon.referralAffiliateId; }
            if (isStringNotEmpty(coupon.referralSubAffiliateId)) { this.referralSubAffiliateId = coupon.referralSubAffiliateId; }
            if (isListNotEmpty(coupon.usedBy)) { this.usedBy = coupon.usedBy; }

            if (isListNotEmpty(coupon.excludedProductIds)) { this.excludedProductIds = coupon.excludedProductIds; }
            if (isListNotEmpty(coupon.excludedVariantIds)) { this.excludedVariantIds = coupon.excludedVariantIds; }
            if (isBooleanTrue(coupon.applyToBundle)) { this.applyToBundle = true; }
            if (isListNotEmpty(coupon.excludedBundleCounts)) { this.excludedBundleCounts = coupon.excludedBundleCounts; }

            if (isBooleanTrue(coupon.newCustomersOnly)) { this.newCustomersOnly = true; }
            if (isBooleanTrue(coupon.starterTitrationLevelOnly)) { this.starterTitrationLevelOnly = true; }

            if (isNumberNotEmpty(coupon.creditPayout)) { this.creditPayout = coupon.creditPayout; }
            if (isNumberNotEmpty(coupon.cashPayout)) { this.cashPayout = coupon.cashPayout; }

            if (isStringNotEmpty(coupon.terms)) { this.terms = coupon.terms; }

            if (isListNotEmpty(coupon.discounts)) { this.discounts = normalizeCouponDiscountRows(coupon.discounts); }

            if (isBooleanTrue(coupon.isReferral)) { this.isReferral = true; }
        }
    }

    toDto = () => {
        return {
            code: this.code,
            discountType: this.discountType,
            discountAmount: this.discountAmount,
            discountTarget: this.discountTarget,
            productIds: this.productIds,
            active: this.active,

            usageCount: this.usageCount,
            expiresAt: this.expiresAt,
            individualUse: this.individualUse,
            usageLimit: this.usageLimit,
            usageLimitPerUser: this.usageLimitPerUser,
            limitUsageToXItems: this.limitUsageToXItems,
            freeShipping: this.freeShipping,
            excludeSaleItems: this.excludeSaleItems,
            minimumAmount: this.minimumAmount,
            maximumAmount: this.maximumAmount,
            emailRestrictions: this.emailRestrictions,
            referralUserId: this.referralUserId,
            referralAffiliateId: this.referralAffiliateId,
            referralSubAffiliateId: this.referralSubAffiliateId,
            usedBy: this.usedBy,

            excludedProductIds: this.excludedProductIds,
            excludedVariantIds: this.excludedVariantIds,
            applyToBundle: this.applyToBundle,
            excludedBundleCounts: this.excludedBundleCounts,

            newCustomersOnly: this.newCustomersOnly,
            starterTitrationLevelOnly: this.starterTitrationLevelOnly,

            creditPayout: this.creditPayout,
            cashPayout: this.cashPayout,

            terms: this.terms,

            discounts: normalizeCouponDiscountRows(this.discounts),

            isReferral: this.isReferral
        };
    }

    isValid = () => {
        if (isStringEmpty(this.code)) {
            throw new Error('Coupon code is required.');
        }
        if (isStringEmpty(this.discountType) || !Enums.DiscountTypes.includesKey(this.discountType)) {
            throw new Error('Discount type is required and must be one of the correct enums.');
        }
        if (isNumberEmptyOrZero(this.discountAmount) || isNumberLessThanOrEqualToZero(this.discountAmount)) {
            throw new Error('Discount amount is missing, negative, or has a zero value. Discount amount must be greater than zero.');
        }
        if (isStringEmpty(this.discountTarget) || !Enums.DiscountTargets.includesKey(this.discountTarget)) {
            throw new Error('Discount target is required and must be one of the correct enums.');
        }
        if (isListNotEmpty(this.discounts)) {
            validateCouponDiscountRows(this.discounts);
        }

        return true;
    }

    getDiscountDisplay = () => {
        if (this.discountType === 'FLAT_AMOUNT') {
            return toFormattedMoney(this.discountAmount) + ' Off';
        } else if (this.discountType === 'PERCENTAGE') {
            return toFormattedPercentage(this.discountAmount) + ' Off';
        }
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Coupon.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Coupon.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Coupon.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedCoupon) => {
        const updatedObj = new Coupon(updatedCoupon);
        if (updatedObj.isValid() && id === updatedCoupon.id) {
            return await update(Coupon.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated coupon must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Coupon.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Coupon.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Coupon.collectionName);
    }

    static getByCode = async (code) => {
        if (isStringNotEmpty(code)) {
            const coupons = await getBySingleQuery(Coupon.collectionName, 'code', toLower(code.trim()), '==');
            if (isListNotEmpty(coupons)) {
                return coupons[0];
            }
        }

        return null;
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Coupon.collectionName, fieldName, fieldValue, comparer);
    }

    static getByDiscountTargetAndProductId = async (discountTarget, productId) => {
        return await getByDoubleQuery(Coupon.collectionName, 'discountTarget', discountTarget, '==', 'productIds', productId, 'array-contains');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Coupon.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { discountTarget, isReferral, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(discountTarget)) {
            queryConstraints.push(where('discountTarget', 'in', discountTarget));
        } else if (isStringNotEmptyOrZeroLength(discountTarget)) {
            queryConstraints.push(where('discountTarget', '==', discountTarget));
        }

        if (isBooleanTrue(isReferral)) {
            queryConstraints.push(where('isReferral', '==', true));
        } else if (isBooleanFalse(isReferral)) {
            queryConstraints.push(where('isReferral', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Coupon.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { discountTarget, isReferral, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(discountTarget)) {
            queryConstraints.push(where('discountTarget', 'in', discountTarget));
        } else if (isStringNotEmptyOrZeroLength(discountTarget)) {
            queryConstraints.push(where('discountTarget', '==', discountTarget));
        }

        if (isBooleanTrue(isReferral)) {
            queryConstraints.push(where('isReferral', '==', true));
        } else if (isBooleanFalse(isReferral)) {
            queryConstraints.push(where('isReferral', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Coupon.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Coupon.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Coupon.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Coupon;
